package com.siriusxm.pia.views.unifiedaggregator.smithy

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.isValidGuid
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.pia.views.unifiedaggregator.SearchStatus
import com.siriusxm.pia.views.unifiedaggregator.entityList
import com.siriusxm.pia.views.unifiedaggregator.entityListItem
import com.siriusxm.pia.views.unifiedaggregator.entitySearch
import com.siriusxm.pia.views.unifiedaggregator.styles.AggregatorAnimation
import com.siriusxm.smithy4kt.*
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal const val ENTITY_ID_SHAPE_ID = "contentingestion.unifiedmodel#entityId"

/**
 * Creates a typed entity-id member editor when a Smithy member has a known target
 * entity type and its shape is either entityId or a list of entityId values.
 */
internal fun typedEntityIdMemberEditor(
    aggregatorService: AggregatorService,
    member: SmithyMember,
): TypedEntityIdMemberEditor? {
    val parentId = member.parent.id
    val typeFilter = entityIdTypeHints["$parentId#${member.name}"] ?: return null
    val memberShape = member.shape
    return if (memberShape.isEntityIdShape() || memberShape.isEntityIdListShape()) {
        TypedEntityIdMemberEditor(aggregatorService, member, typeFilter)
    } else {
        null
    }
}

private fun SmithyEntity.isEntityIdShape(): Boolean {
    return (this as? SmithyShape)?.id == ENTITY_ID_SHAPE_ID
}

private fun SmithyEntity.isEntityIdListShape(): Boolean {
    return this is SmithyList && member.isEntityIdShape()
}

/**
 * Renders entityId values with lookup/copy affordances and provides an edit-mode
 * picker that can be constrained to a known target entity type.
 */
class EntityIdViewer(
    private val aggregatorService: AggregatorService,
    shape: SmithyEntity,
    private val entityTypeFilter: String? = null
) : SmithyViewer<SmithyEntity>(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        var resolvedId by remember { mutableStateOf((value as? JsonPrimitive)?.contentOrNull) }
        var loading by remember { mutableStateOf(false) }

        val baseId = (value as? JsonPrimitive)?.contentOrNull
        val id = resolvedId ?: return

        A("#aggregator/entity/${encodeURIComponent(baseId.orEmpty())}") {
            Text(id)
        }

        Span({
            classes(JsonViewStyles.copyContentIcon)
            if (loading) classes(AggregatorAnimation.spin)
        }) {
            icon("autorenew") {
                size = IconSize.TINY
                title = "Lookup mapped identifier"
                style { lineHeight("inherit") }

                action {
                    if (baseId != resolvedId) {
                        resolvedId = baseId
                    } else if (baseId != null) {
                        loading = true
                        try {
                            val response = aggregatorService.clientApi.getMapping(baseId)
                            resolvedId = if (response.sourceId == baseId) {
                                response.atlasId
                            } else {
                                response.sourceId
                            }
                        } catch (_: Throwable) {
                            // Ignore lookup failures; the raw id is still useful to show.
                        }
                        loading = false
                    }
                }
            }
        }

        copyContentIcon(id)
    }

    @Composable
    override fun edit(baseValue: JsonElement?, modification: JsonElement?, options: SmithyViewOptions) {
        val effectiveValue = when {
            modification is JsonPrimitive && modification.contentOrNull != null -> modification.contentOrNull
            modification == null -> (baseValue as? JsonPrimitive)?.contentOrNull
            else -> null
        }

        if (effectiveValue != null) {
            ResolvedEntityRow(effectiveValue)
        } else {
            EntitySearchPicker()
        }
    }

    /**
     * Displays a selected entity reference, resolves it to entity details when possible,
     * and surfaces type mismatches when the caller supplied a target type.
     */
    @Composable
    private fun ResolvedEntityRow(currentId: String) {
        var resolved by remember(currentId) { mutableStateOf<Entity?>(null) }
        var loadFailed by remember(currentId) { mutableStateOf(false) }

        LaunchedEffect(currentId) {
            try {
                val lookupId = if (isValidGuid(currentId)) {
                    runCatching {
                        aggregatorService.clientApi.getMapping(currentId).sourceId
                    }.getOrDefault(currentId)
                } else {
                    currentId
                }
                resolved = aggregatorService.clientApi.getEntityById(lookupId).asEntities()
                    .maxByOrNull { it.version }
                if (resolved == null) loadFailed = true
            } catch (_: Throwable) {
                loadFailed = true
            }
        }

        LaunchedEffect(resolved) {
            val r = resolved
            if (r != null && isValidGuid(currentId) && !isValidGuid(r.id) && r.id != currentId) {
                onUpdate(JsonPrimitive(r.id))
            }
        }

        val typeMismatch = resolved != null && entityTypeFilter != null &&
            !resolved!!.type.equals(entityTypeFilter, ignoreCase = true)

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                padding(6.px)
            }
        }) {
            val entity = resolved
            if (entity != null) {
                entityListItem(
                    aggregatorService, entity, includeLink = false,
                    includeId = true,
                )
                if (entity.id != currentId) {
                    Div({
                        style {
                            color(SXMUI.subtleTextColor.value())
                            fontSize(11.px)
                        }
                    }) {
                        Text("Stored as: $currentId")
                    }
                }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                    }
                }) {
                    pill(currentId)
                    if (loadFailed) {
                        Span({
                            style { color(SXMUI.stopColor.value()) }
                        }) {
                            Text("(could not resolve)")
                        }
                    }
                }
            }

            if (typeMismatch) {
                Div({
                    style {
                        color(SXMUI.stopColor.value())
                        fontSize(12.px)
                    }
                }) {
                    Text("Expected type: $entityTypeFilter, got: ${resolved!!.type}")
                }
            }
        }
    }

    /**
     * Renders the entity search control and result list used when an entityId field
     * has no current value or has been cleared for replacement.
     */
    @Composable
    private fun EntitySearchPicker() {
        var searchResults by remember { mutableStateOf<SearchStatus?>(null) }
        var searchKey by remember { mutableStateOf(0) }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                padding(6.px)
            }
        }) {
            key(searchKey) {
                entitySearch(
                    aggregatorService,
                    initType = entityTypeFilter,
                    showTypes = entityTypeFilter == null,
                ) {
                    searchResults = it
                }
            }

            searchResults?.let { status ->
                if (!status.loading && status.results.isNotEmpty()) {
                    Div({ classes(AggregatorStyles.searchResultsScrollable) }) {
                        entityList(
                            aggregatorService, status.results, includeLinks = false
                        ) { picked ->
                            onUpdate(JsonPrimitive(picked.id))
                            searchResults = null
                            searchKey++
                        }
                    }
                }
            }

            if (entityTypeFilter == null) {
                Div({
                    style {
                        color(SXMUI.subtleTextColor.value())
                        fontSize(11.px)
                    }
                }) {
                    Text("Pick a type to constrain the search.")
                }
            }
        }
    }
}

/**
 * Maps "<containing struct shape id>#<member name>" to the entity type a contained
 * entityId field is expected to reference.
 *
 * TODO: Add more partial-specific hints here until the model carries target type
 * metadata directly.
 */
internal val entityIdTypeHints: Map<String, String> = mapOf(
    "contentingestion.unifiedmodel#FeaturedTalent#talents" to "talent"
)

/**
 * Edits Smithy members that contain entityId references and have a known target
 * entity type, rendering either a single constrained picker or a list of pickers.
 */
class TypedEntityIdMemberEditor(
    private val aggregatorService: AggregatorService,
    private val member: SmithyMember,
    private val entityTypeFilter: String,
) : SmithyViewer<SmithyEntity>(member) {

    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        SmithyMemberEditor(member).view(value, options)
    }

    @Composable
    override fun edit(baseValue: JsonElement?, modification: JsonElement?, options: SmithyViewOptions) {
        when (val shape = member.shape) {
            is SmithyList -> editList(shape, baseValue, modification, options)
            else -> {
                val viewer = EntityIdViewer(aggregatorService, shape, entityTypeFilter)
                viewer.onUpdate = onUpdate
                viewer.edit(baseValue, modification, options)
            }
        }
    }

    /**
     * Renders a typed entity-id list while preserving base values as the visible
     * source until the user makes the first list edit.
     */
    @Composable
    private fun editList(
        listShape: SmithyList,
        baseValue: JsonElement?,
        modification: JsonElement?,
        options: SmithyViewOptions
    ) {
        val list: JsonArray? = when {
            modification is JsonArray -> modification
            modification == null || modification is JsonNull -> baseValue as? JsonArray
            else -> return
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                margin(3.px, 0.px)
                gap(6.px)
                flexDirection(FlexDirection.Column)
            }
        }) {
            list?.forEachIndexed { listIndex, listValue ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.FlexStart)
                        gap(6.px)
                    }
                }) {
                    Div({ style { flexGrow(1) } }) {
                        val itemEditor = EntityIdViewer(
                            aggregatorService, listShape.member, entityTypeFilter
                        )
                        itemEditor.onUpdate = { newValue ->
                            val newList = list.mapIndexed { i, v ->
                                if (i == listIndex) (newValue ?: JsonNull) else v
                            }
                            onUpdate(JsonArray(newList))
                        }
                        itemEditor.edit(null, listValue, options)
                    }
                    icon("close") {
                        size = IconSize.SMALL
                        title = "Remove"
                        action {
                            val newList = list.filterIndexed { i, _ -> i != listIndex }
                            onUpdate(JsonArray(newList))
                        }
                    }
                }
            }
            Div {
                icon("add") {
                    title = "Add"
                    action {
                        onUpdate(JsonArray((list.orEmpty()) + JsonNull))
                    }
                }
            }
        }
    }
}
